import { usePress } from '@react-aria/interactions'
import {
  AddColumnAfter,
  AddColumnBefore,
  AddRowAfter,
  AddRowBefore,
  BorderStyle,
  BorderThickness,
  Delete,
  DeleteColumn,
  DeleteRow,
  FitTableToDoc,
  MergeCells,
  TableAlignmentTop,
  TextColor,
  VerticalAlignmentTop,
} from 'almanac-ui/icons'
import BorderColor from 'almanac-ui/icons/24/BorderColor'
import { ReactComponent as EllipsisIcon } from 'assets/icons/more24.svg'
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumnsInSelection,
  deleteRowsInSelection,
  distributeColumns,
  mergeCells,
  splitCell,
} from 'common/Prose/Commands/table/mutation'
import { tryGetTrackChangesState } from 'common/Prose/Plugins/TrackChanges/constants'
import { canMergeCells } from 'common/Prose/Plugins/tableSelection/tableSelectionUtils'
import { ActiveColorsIndicator } from 'common/Prose/components/ActiveColorsIndicator/ActiveColorsIndicator'
import { TableBorderColorPicker } from 'common/Prose/components/TableBorderColorPicker/TableBorderColorPicker'
import { TableBorderStylePicker } from 'common/Prose/components/TableBorderStylePicker/TableBorderStylePicker'
import { TableBorderThicknessPicker } from 'common/Prose/components/TableBorderThicknessPicker/TableBorderThicknessPicker'
import { TableCellColorPicker } from 'common/Prose/components/TableCellColorPicker/TableCellColorPicker'
import { TableContextMenu } from 'common/Prose/components/TableContextMenu/TableContextMenu'
import { TextColorPicker } from 'common/Prose/components/TextColorPicker/TextColorPicker'
import { ProseDropdownPrompt } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/ProseDropdownPrompt/ProseDropdownPrompt'
import { VerticalAlignmentPicker } from 'common/Prose/components/VerticalAlignmentPicker/VerticalAlignmentPicker'
import { useVerticalAlignmentPicker2 } from 'common/Prose/components/VerticalAlignmentPicker2/VerticalAlignmentPicker'
import { isTableSelection as getIsTableSelection } from 'common/Prose/utils/table'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { InlineFormatDivider } from 'components/InlineFormatBar/components/InlineFormatDivider/InlineFormatDivider'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { pickBy } from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { type EditorView } from 'prosemirror-view'
import { useContext, useRef } from 'react'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import Styles from './TableFormatBar.module.scss'
import { CellColorIcon } from './components/CellColorIcon/CellColorIcon'

interface Props {
  view: EditorView
}

const MoreOptionsButton = () => {
  const ref = useRef<HTMLButtonElement>(null)
  const { pressProps } = usePress({ preventFocusOnPress: true, ref })
  const filteredProps = pickBy(pressProps, (_, key) => key.startsWith('on'))
  return (
    <button ref={ref} {...filteredProps} style={{ height: 28, padding: 2 }}>
      <EllipsisIcon />
    </button>
  )
}

// NOTE: this component is used in the table format bar AND the cell format bar
// and renders some buttons conditionally
const TableFormatBarOld = observer(({ view }: Props) => {
  const stateContext = useContext(AlmanacEditorStateContext)
  if (!stateContext) return null
  const selection = view.state.selection
  const isTableSelection = computed(() => getIsTableSelection(stateContext.selection)).get()

  if (!view.editable || (!(selection instanceof CellSelection) && !isTableSelection)) return null

  const cellSelection = !isTableSelection ? (selection as CellSelection) : null

  const insertColumnAfterLabel = `Insert column ${isTableSelection ? 'at end' : 'right'}`
  const insertColumnBeforeLabel = `Insert column ${isTableSelection ? 'at beginning' : 'left'}`
  const insertRowAfterLabel = `Insert row ${isTableSelection ? 'at end' : 'below'}`
  const insertRowBeforeLabel = `Insert row ${isTableSelection ? 'at beginning' : 'above'}`

  const trackChangesState = tryGetTrackChangesState(view.state)
  const trackingChanges = trackChangesState?.store.trackingChanges || false

  return (
    <InlineFormatBar>
      <ProseDropdownPrompt
        withChevron
        view={view}
        label="Set vertical alignment"
        anchorChildren={<VerticalAlignmentTop />}
        placement="bottom-start"
        dropdownComponent={(close) => (
          <div className={Styles.dropdownContainer}>
            <VerticalAlignmentPicker includeDropdownStyles={false} view={view} closePicker={close} />
          </div>
        )}
      />
      {!isTableSelection && // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (cellSelection!.isSingleMergedCellSelection ? (
          <InlineFormatButton
            data-testid="Split cell"
            onClick={() => splitCell(view.state, view.dispatch)}
            label="Split cell"
            isDisabled={trackingChanges}
          >
            <MergeCells />
          </InlineFormatButton>
        ) : (
          <InlineFormatButton
            data-testid="Merge cells"
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            isDisabled={cellSelection!.isSingleCellSelection || !canMergeCells(cellSelection!) || trackingChanges}
            onClick={() => mergeCells(view.state, view.dispatch)}
            label="Merge cells"
          >
            <MergeCells />
          </InlineFormatButton>
        ))}

      <InlineFormatDivider />

      <DropdownTrigger>
        <InlineFormatButton data-testid="Set cell color" label="Set cell color">
          <CellColorIcon view={view} />
        </InlineFormatButton>
        {(close) => <TableCellColorPicker includeDropdownStyles={false} view={view} closePicker={close} />}
      </DropdownTrigger>

      <DropdownTrigger>
        <InlineFormatButton data-testid="Set border color" label="Set border color">
          <BorderColor style={{ margin: '0 -4px' }} />
        </InlineFormatButton>
        {(close) => <TableBorderColorPicker includeDropdownStyles={false} view={view} closePicker={close} />}
      </DropdownTrigger>

      <DropdownTrigger popoverClassName={Styles.dropdownContainer}>
        <InlineFormatButton data-testid="Set border thickness" label="Set border thickness">
          <BorderThickness />
        </InlineFormatButton>
        {(close) => <TableBorderThicknessPicker includeDropdownStyles={false} view={view} closePicker={close} />}
      </DropdownTrigger>

      <DropdownTrigger popoverClassName={Styles.dropdownContainer}>
        <InlineFormatButton data-testid="Set border style" label="Set border style">
          <BorderStyle />
        </InlineFormatButton>
        {(close) => <TableBorderStylePicker includeDropdownStyles={false} view={view} closePicker={close} />}
      </DropdownTrigger>

      <InlineFormatDivider />

      {isTableSelection && (
        <>
          <InlineFormatButton
            data-testid="Fit to document"
            onClick={() => {
              const tablePos = view.state.selection.from
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const tableEl = view.nodeDOM(tablePos)! as HTMLElement

              distributeColumns(tableEl.offsetWidth, tablePos)(view.state, view.dispatch)
            }}
            label="Fit to document"
          >
            <FitTableToDoc />
          </InlineFormatButton>

          <InlineFormatDivider />
        </>
      )}

      <InlineFormatButton
        data-testid={insertColumnBeforeLabel}
        onClick={() => addColumnBefore(view.state, view.dispatch)}
        label={insertColumnBeforeLabel}
      >
        <AddColumnBefore />
      </InlineFormatButton>

      <InlineFormatButton
        data-testid={insertColumnAfterLabel}
        onClick={() => addColumnAfter(view.state, view.dispatch)}
        label={insertColumnAfterLabel}
      >
        <AddColumnAfter />
      </InlineFormatButton>

      <InlineFormatButton
        data-testid={insertRowAfterLabel}
        onClick={() => addRowAfter(view.state, view.dispatch)}
        label={insertRowAfterLabel}
      >
        <AddRowAfter />
      </InlineFormatButton>

      <InlineFormatButton
        data-testid={insertRowBeforeLabel}
        onClick={() => addRowBefore(view.state, view.dispatch)}
        label={insertRowBeforeLabel}
      >
        <AddRowBefore />
      </InlineFormatButton>

      <InlineFormatDivider />

      {isTableSelection ? (
        <InlineFormatButton
          data-testid="Remove table"
          onClick={() => view.dispatch(view.state.tr.deleteSelection())}
          label="Remove table"
        >
          <Delete />
        </InlineFormatButton>
      ) : (
        <>
          <InlineFormatButton
            data-testid="Remove column"
            onClick={() => deleteColumnsInSelection(view.state, view.dispatch)}
            label="Remove column"
            isDisabled={trackingChanges}
          >
            <DeleteColumn />
          </InlineFormatButton>
          <InlineFormatButton
            data-testid="Remove row"
            onClick={() => deleteRowsInSelection(view.state, view.dispatch)}
            label="Remove row"
          >
            <DeleteRow />
          </InlineFormatButton>
        </>
      )}
    </InlineFormatBar>
  )
})

const TableFormatBarNew = observer(({ view }: Props) => {
  const stateContext = useContext(AlmanacEditorStateContext)
  if (!stateContext) return null
  const selection = view.state.selection
  const isTableSelection = computed(() => getIsTableSelection(stateContext.selection)).get()
  const editorState = useContext(AlmanacEditorStateContext)

  const trackChangesState = tryGetTrackChangesState(view.state)
  const trackingChanges: boolean = trackChangesState?.store.trackingChanges || false

  const tableContextMenu = <TableContextMenu view={view} trackingChanges={trackingChanges} />
  const alignmentPicker = useVerticalAlignmentPicker2({ view })

  if (!view.editable || (!(selection instanceof CellSelection) && !isTableSelection)) return null

  const activeColors = computed(() => Array.from(editorState?.activeColors ?? [])).get()

  return (
    <InlineFormatBar>
      <DropdownTrigger>
        <InlineFormatButton data-testid="Set text color" label="Set text color">
          <div>
            <TextColor />
            <ActiveColorsIndicator colors={activeColors} />
          </div>
        </InlineFormatButton>
        {(close) => <TextColorPicker activeColors={activeColors} view={view} callback={close} />}
      </DropdownTrigger>

      <DropdownTrigger>
        <InlineFormatButton data-testid="Set cell color" label="Set cell color">
          <CellColorIcon view={view} />
        </InlineFormatButton>
        {(close) => (
          <TableCellColorPicker includeDropdownStyles={false} view={view} closePicker={close} focusView={false} />
        )}
      </DropdownTrigger>

      <DropdownTrigger>
        <InlineFormatButton data-testid="Set border color" label="Set border color">
          <BorderColor style={{ margin: '0 -4px' }} />
        </InlineFormatButton>
        {(close) => (
          <TableBorderColorPicker includeDropdownStyles={false} view={view} closePicker={close} focusView={false} />
        )}
      </DropdownTrigger>

      <DropdownTrigger>
        <InlineFormatButton data-testid="Set border color" label="Set border color">
          <TableAlignmentTop />
        </InlineFormatButton>
        {(close) => alignmentPicker}
      </DropdownTrigger>

      <InlineFormatDivider />

      <MenuTrigger placement="bottom">
        <MoreOptionsButton />
        {tableContextMenu}
      </MenuTrigger>
    </InlineFormatBar>
  )
})
TableFormatBarNew.displayName = 'TableFormatBarNew'

export function TableFormatBar(props: Props) {
  const newTableMenu = useFlipperFlag('new_table_menu')
  return newTableMenu ? <TableFormatBarNew {...props} /> : <TableFormatBarOld {...props} />
}
